import type {
  DropdownInputSettings,
  EditorContent,
  Field,
  MergeValues,
  NodeWithText,
  Row,
  Section,
  Context,
  EditorBlock,
  LinkedResource,
} from '@getaccept/editor-lib';
import { EditorHelper as EditorShared, FieldInputType, MergeCategory } from '@getaccept/editor-lib';
import { MoveDirection } from '@getaccept/lib-shared/src/enums/move-direction';
import type { OptionMenuItem } from '@getaccept/lib-shared/src/options-menu/types/option-menu-item';
import { PortalConstants } from '@getaccept/lib-shared/src/portal/constants/portal.constants';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { UpdateNodeAction } from '../store/types/actions';
import { ProsemirrorHelper } from './prosemirror.helper';

const moveToTop = 'chevron-up-line';
const moveUp = 'chevron-up';
const moveDown = 'chevron-down';
const moveToBottom = 'chevron-down-line';
export class EditorHelper {
  static nodeMoveOptions: OptionMenuItem[] = [
    {
      icon: moveToTop,
      title: 'move-to-top',
      callback: MoveDirection.ToTop,
    },
    {
      icon: moveUp,
      title: 'move-up',
      callback: MoveDirection.Up,
    },
    {
      icon: moveDown,
      title: 'move-down',
      callback: MoveDirection.Down,
    },
    {
      icon: moveToBottom,
      title: 'move-to-bottom',
      callback: MoveDirection.ToBottom,
    },
  ];

  static pricingTableMoveSectionOptions: OptionMenuItem[] = [
    {
      icon: moveToTop,
      title: 'move-pricing-group-to-the-top',
      callback: MoveDirection.ToTop,
    },
    {
      icon: moveUp,
      title: 'move-pricing-group-up',
      callback: MoveDirection.Up,
    },
    {
      icon: moveDown,
      title: 'move-pricing-group-down',
      callback: MoveDirection.Down,
    },
    {
      icon: moveToBottom,
      title: 'move-pricing-group-to-the-bottom',
      callback: MoveDirection.ToBottom,
    },
  ];

  static pricingTableMoveRowOptions: OptionMenuItem[] = [
    {
      icon: moveToTop,
      title: 'move-row-to-the-top',
      callback: MoveDirection.ToTop,
    },
    {
      icon: moveUp,
      title: 'move-row-up',
      callback: MoveDirection.Up,
    },
    {
      icon: moveDown,
      title: 'move-row-down',
      callback: MoveDirection.Down,
    },
    {
      icon: moveToBottom,
      title: 'move-row-to-the-bottom',
      callback: MoveDirection.ToBottom,
    },
  ];

  static getRecipientData(person: Recipient = {}): { name: string; thumbUrl?: string } {
    if (!person.email && !person.mobile && person.roleName) {
      switch (person.role) {
        case RecipientRole.CC:
          return { name: 'OV' };
        case RecipientRole.Approver:
        case RecipientRole.InternalApproverAPIFallback:
        case RecipientRole.ExternalApprover:
          return { name: 'AP' };
        case RecipientRole.Signer:
          return { name: 'SI' };
        default:
          return { name: person.roleName.substring(0, 2).toLocaleUpperCase() || '' };
      }
    }

    return { name: person.fullName || person.email || '', thumbUrl: person.thumbUrl };
  }

  static filterById(recipients: Recipient[], id: string) {
    return recipients.filter(recipient => recipient.id === id);
  }

  static getSenderIfUserId(sender: User, id = '') {
    return sender.id === id ? sender : undefined;
  }

  static getAssignIds(
    mergeValues: MergeValues,
    context: Context
  ): { userId: string; recipientId: string; participantId?: string } {
    if (EditorShared.isDealroomEditContentContext(context)) {
      return {
        userId: null,
        participantId: null,
        recipientId: null,
      };
    }
    if (!mergeValues.recipients?.length) {
      return {
        userId: mergeValues.sender.id,
        recipientId: null,
      };
    }

    const recipient = mergeValues.recipients[0];
    return {
      userId: null,
      recipientId: recipient.id,
    };
  }

  static getAssignIdsForSignature({
    userId = null,
    recipientId = null,
  }: {
    userId?: string;
    recipientId?: string;
  }) {
    return {
      userId,
      recipientId,
      inputSettings: { type: FieldInputType.Signature, hide: false },
    };
  }

  static mergeTagExpression = new RegExp(EditorShared.mergeTagExpression, 'g');

  static getMergeTagIdsFromValue(inputValue: string): string[] {
    return inputValue?.match(EditorHelper.mergeTagExpression)?.map(id => id.slice(2, -2)) || [];
  }

  static getMergeTagIdsFromInput(input: Field): string[] {
    if (!input) {
      return [];
    }

    const valueIds = EditorHelper.getMergeTagIdsFromValue(input.value);
    const labelIds = ProsemirrorHelper.getTagIds(input.inputSettings.richTextLabel);
    const helpTextIds = ProsemirrorHelper.getTagIds(input.inputSettings.helpText);
    const optionsIds = EditorHelper.getMergeTagIdsFromValue(
      (input.inputSettings as DropdownInputSettings).options?.join('')
    );

    return [...labelIds, ...valueIds, ...optionsIds, ...helpTextIds];
  }

  static isClickWithinEditor(element: HTMLElement) {
    const hasEditorSpecificClass = ({ classList }: HTMLElement) =>
      classList.contains('editor-block') ||
      classList.contains('editor-options-popper') ||
      classList.contains('editor-modal');

    let isClickWithin = false;

    if (hasEditorSpecificClass(element)) {
      isClickWithin = true;
    }

    while ((element = element.parentElement)) {
      if (hasEditorSpecificClass(element)) {
        isClickWithin = true;
      }
    }

    return isClickWithin;
  }

  static isClickWithinModal(element: HTMLElement) {
    const modal = document.getElementById(PortalConstants.elementId);
    if (!element || !modal) {
      return false;
    }

    return modal === element || modal.contains(element);
  }

  static moveSectionUp(sourceSectionId: string, sections: Section[]) {
    const selectedIndex = sections.findIndex(({ id }) => id === sourceSectionId);
    const targetSectionId = sections[selectedIndex - 1].id;

    return {
      targetSectionId,
      append: false,
    };
  }

  static moveSectionDown(sourceSectionId: string, sections: Section[]) {
    const selectedIndex = sections.findIndex(({ id }) => id === sourceSectionId);
    const targetSectionId = sections[selectedIndex + 1].id;

    return {
      targetSectionId,
      append: true,
    };
  }

  static getMoveSectionPayload({
    direction,
    sourceSectionId,
    sections,
  }: {
    direction: MoveDirection;
    sourceSectionId: string;
    sections: Section[];
  }) {
    switch (direction) {
      case MoveDirection.ToTop:
        return {
          targetSectionId: sections[0].id,
          append: false,
        };

      case MoveDirection.Up:
        return EditorHelper.moveSectionUp(sourceSectionId, sections);

      case MoveDirection.Down:
        return EditorHelper.moveSectionDown(sourceSectionId, sections);

      case MoveDirection.ToBottom:
        return {
          targetSectionId: sections[sections.length - 1].id,
          append: true,
        };
    }
  }

  static targetNodeExist(
    sections: Section[],
    { targetSectionId, targetRowId, targetCellId, targetNodeId }: UpdateNodeAction
  ): boolean {
    return !!(sections || [])
      .find(({ id }) => id === targetSectionId)
      ?.rows.find(({ id }) => id === targetRowId)
      ?.cells.find(({ id }) => id === targetCellId)
      ?.nodes.find(({ id }) => id === targetNodeId);
  }

  static removeNullPropertiesFromNodes(content: EditorContent) {
    return {
      ...content,
      sections: content.sections.map(section => ({
        ...section,
        rows: section.rows.map(row => ({
          ...row,
          cells: row.cells.map(cell => ({
            ...cell,
            nodes: cell.nodes
              ? cell.nodes.map(node => {
                  Object.keys(node).forEach(key => {
                    if (node[key] === null) {
                      delete node[key];
                    }
                  });
                  return node;
                })
              : [],
          })),
        })),
      })),
    };
  }

  static updateRowFieldsWithCustomTags = (rows: Row[], customMergeTags: Field[]) =>
    rows.map(row => ({
      ...row,
      cells: row.cells.map(cell => ({
        ...cell,
        nodes: cell.nodes.map((node: NodeWithText) =>
          node.fields
            ? {
                ...node,
                fields: node.fields.map(field =>
                  field.customName
                    ? {
                        ...field,
                        value:
                          customMergeTags.find(tag => tag.customName === field.customName)?.value ??
                          field.value,
                      }
                    : field
                ),
              }
            : node
        ),
      })),
    }));

  static getRecipientFieldsFromRow = (row: Row): Field[] =>
    row.cells.flatMap(cell =>
      cell.nodes.flatMap((node: NodeWithText) =>
        (node.fields || []).filter(field => field.category === MergeCategory.Recipient)
      )
    );

  static getRecipientFieldsFromSection = (section: Section): Field[] =>
    section.rows.flatMap(EditorHelper.getRecipientFieldsFromRow);

  static getRecipientFieldsFromContent = (content: EditorContent): Field[] =>
    content.sections.flatMap(EditorHelper.getRecipientFieldsFromSection);

  static getSection = (sections: Section[], sectionId: string) =>
    sections.find(section => section.id === sectionId);

  static isSectionHidden = (blocks: EditorBlock[], blockId: string, targetSectionId: string) => {
    const block = blocks.find(block => block.id === blockId);
    const section = EditorHelper.getSection(block.content.sections, targetSectionId);
    return section?.hidden;
  };

  static isSectionInLinkedResourceHidden = (
    linkedResources: LinkedResource[],
    blockId: string,
    targetSectionId: string
  ) => {
    const linkedResource = linkedResources.find(resource => resource.editorBlockId === blockId);
    if (!linkedResource) return;
    const section = EditorHelper.getSection(linkedResource.sections, targetSectionId);
    return section?.hidden;
  };
}
